import { VText } from '@vake/ui-kit';
import { FC, ReactNode, useEffect } from 'react';

export const PrivacyStatement: FC = () => {
    useEffect(() => {
        document.title = 'Privacy Statement';
    }, []);
    return (
        <div className="flex flex-col pt-32 w-[700px] mx-auto gap-10">
            <VText variant="H1">Privacy Statement for VAKE</VText>
            {data.map((item, index) => (
                <div key={index}>
                    <VText variant="H2">{item.title}</VText>
                    <VText>{item.text}</VText>
                    {item.bulletList}
                </div>
            ))}
        </div>
    );
};

const data: { title: string; text: string; bulletList?: ReactNode }[] = [
    {
        title: 'Updated: 1. October 2025',
        text: 'VAKE AS (“VAKE”, “We”, “Us”, “Our”) takes the privacy of our users (“User”, “You”) seriously. We have created this Privacy Statement (“Statement”) to explain how we collect, use, and disclose information when You use our websites, digital services, and applications (the “Service”). VAKE provides maritime surveillance, intelligence, and analytics solutions. To deliver these services, we process certain categories of information in a manner consistent with applicable laws, including the EU General Data Protection Regulation (GDPR)',
    },
    {
        title: '1. Information We Collect',
        text: 'We may collect and process the following categories of information: Personal Data provided by You: such as name, email address, organization, login credentials, or other details You submit when registering for or using the Service. Usage and activity data: interactions with our platforms and features, reports viewed, feedback You provide (e.g., via surveys or forms), and behavioral patterns collected to improve the user experience and product performance. Device and technical data: such as browser type, operating system, IP address, and log files generated during Service use. Other data You choose to provide: when communicating with us (e.g. support inquiries, feedback). We collect data primarily from You directly, or it is generated automatically when You interact with the Service.',
    },
    {
        title: '2. Use of Your Information',
        text: 'We use the information we collect for various purposes, including to provide and improve our Service, to communicate with You, to personalize Your experience, to analyze and improve our products and services, and to comply with legal obligations.',
    },
    {
        title: '3. Sharing Your Information',
        text: 'We may share Your information with third parties, such as technical service providers, in order to provide and improve the Service. We may also share Your information with third parties as required by law. We do not sell or disclose personal data to third parties for marketing or other purposes unrelated to the Service. We will restrict the amount of data given to service providers and only share information that is important to make the best service possible. When we give third parties access to personal data in this way, we make use of data processing agreements so that we ensure that the third parties comply with our high standards, requirements for the processing of personal data, and applicable legislation. Disclosure of personal data may also take place in connection with a business transfer, merger, acquisition, amalgamation of, with or to another company. Our main purpose is to keep the oceans safe. It is therefore conceivable that we will share information with collaboration partners and/or representatives from academia and research institutions for this purpose. In that case, we will inform you about who our partners are and what information we will share. We do not share your personal data with third parties outside the EU/EEA area (so-called third countries).',
        bulletList: (
            <>
                <VText>
                    Key service providers and partners who provide hosting, infrastructure, analytics, or support
                    services:
                </VText>
                <ul>
                    <li>
                        <VText>Google Cloud Services (Europe)</VText>
                    </li>
                    <li>
                        <VText>Hotjar (Europe)</VText>
                    </li>
                    <li>
                        <VText>Nemme (Norway)</VText>
                    </li>
                    <li>
                        <VText>Sentry (Europe)</VText>
                    </li>
                    <li>
                        <VText>ClickUp (USA)</VText>
                    </li>
                </ul>
            </>
        ),
    },
    {
        title: '4. Data Retention',
        text: 'We will delete or anonymise Personal Data when we no longer have a valid purpose and basis for processing the personal data. We will retain Your Personal Data for as long as is necessary to provide the Service. This means that the storage time for different types of Personal Data will vary. For example, we will anonymise and/or delete personal data collected in order to fulfil our contractual obligations towards you as a user of our Services when you delete your User Account yourself. In order to further develop our digital services, we continuously use analytical and research data. We use anonymized IDs or Pseudonym IDs whenever possible, to protect Your privacy while enabling long-term improvements. Such anonymisation of personal data enables us to exploit the value inherent in data analysis in a privacy-friendly way. Note that there may be regulatory requirements which mean that we are nevertheless obliged to store your personal data for a certain period.',
    },
    {
        title: '5. Data Security',
        text: 'We work systematically to safeguard information and implement appropriate technical and organisational measures to protect Your Personal Data from unauthorised access, use, alteration, or destruction. Use of the Service requires You to log in with your personal credentials, in order to protect against unauthorised access. You yourself are responsible for keeping the login information (username and password), access codes and the like confidential.',
    },
    {
        title: '6. Your Rights',
        text: 'You have the right to access, correct, and delete Your Personal Data, as well as the right to object to the processing of Your Personal Data. You also have the right to request that Your Personal Data be transferred to another controller. You have several rights related to our processing of your personal information. Here you get information about which these are and how you can exercise these rights to us. If you want to exercise some of your rights, this can be done by contacting us. You are entitled an answer from us without undue delay, and no later than one month.',
        bulletList: (
            <>
                <ul>
                    <li>
                        <VText>
                            Access: You have the right to know what information we have registered about you (with the
                            restrictions provided by current legislation).
                        </VText>
                    </li>
                    <li>
                        <VText>
                            Correction: You may require incorrect, deficient or outdated personal information to be
                            corrected.
                        </VText>
                    </li>
                    <li>
                        <VText>
                            Protesting: You have the right to protest our processing of your personal information if we
                            process information about you on the basis of a balance of interest.
                        </VText>
                    </li>
                    <li>
                        <VText>
                            Withdraw your consent: You can withdraw consents you have given us to process your personal
                            information, at any time.
                        </VText>
                    </li>
                    <li>
                        <VText>
                            Deletion: If you protest the processing of your personal information, or you withdraw your
                            consent, information that we have stored about you will be deleted on the condition that we
                            have no other basis for retaining them.
                        </VText>
                    </li>
                    <li>
                        <VText>
                            Data portability: If we process information about you that you have provided us with the
                            basis of consent or agreement, you can ask us to transfer information about you to you or to
                            other processing officer, as long as this is technically possible.
                        </VText>
                    </li>
                    <li>
                        <VText>
                            The right to complain to a supervisory authority: If you believe our processing of your
                            personal data should be in violation of the Privacy Regulations, we would like to hear from
                            you. You have also the opportunity to complain about this to the Data Inspectorate.
                            Complaints can be submitted on the Data Inspectorate's website; www.datatilsynet.no.
                        </VText>
                    </li>
                </ul>
            </>
        ),
    },
    {
        title: '7. Contact Us',
        text: 'If You have questions about this Statement, or if You wish to exercise Your rights, please contact us at connect@vake.ai. You can also use the following contact details:',
        bulletList: (
            <>
                <ul>
                    <li>
                        <VText>VAKE AS</VText>
                    </li>
                    <li>
                        <VText>Universitetsgata 2</VText>
                    </li>
                    <li>
                        <VText>0164 Oslo</VText>
                    </li>
                    <li>
                        <VText>Norway</VText>
                    </li>
                </ul>
            </>
        ),
    },
    {
        title: '8. Jurisdiction',
        text: 'This Statement shall be governed by the laws of Norway.',
    },
    {
        title: '9. Changes to this Statement',
        text: 'We may update this Statement from time to time. We will inform You of any changes by posting the new Statement on the Service. You are advised to review this Statement periodically for any changes. If we make significant changes to the Privacy Policy, You will receive notification directly through available channels such as e-mail. When we make changes to this privacy policy, we will change the revision date at the top of this page, and a modified privacy policy will take effect from the revision date. This privacy statement is intended to provide you with information on how we collect, use and disclose information that we may collect from you in connection with your use of the VAKE services. ',
    },
];
